import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, SignIn, SignOut, CheckCircle, Backspace, QrCode, ArrowsClockwise, Copy } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "@/context/AuthContext";
import { isAdmin } from "@/lib/roles";

const stateChip = {
  in: "bg-emerald-500/20 text-emerald-300 border-emerald-400/40",
  out: "bg-white/10 text-white/60 border-white/20",
  absent: "bg-red-500/20 text-red-300 border-red-400/40",
  none: "bg-amber-500/20 text-amber-200 border-amber-400/40",
};
const stateLabel = { in: "Here", out: "Left", absent: "Absent", none: "Not arrived" };

export default function Kiosk() {
  const { user } = useAuth();
  const [roster, setRoster] = useState([]);
  const [child, setChild] = useState(null);
  const [kind, setKind] = useState("in");
  const [code, setCode] = useState("");
  const [guardian, setGuardian] = useState("");
  const [done, setDone] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [token, setToken] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/kiosk/roster");
    setRoster(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/kiosk/token").then(({ data }) => setToken(data.token)).catch(() => {});
  }, []);

  const rotate = async () => {
    try {
      const { data } = await api.post("/kiosk/token/rotate");
      setToken(data.token);
      toast.success("New QR code generated — the old one no longer works");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const qrUrl = token ? `${window.location.origin}/checkin?token=${token}` : "";

  const reset = () => { setChild(null); setCode(""); setGuardian(""); };

  const submit = async (fullCode) => {
    try {
      await api.post("/kiosk/checkin", { student_id: child.id, code: fullCode, kind, guardian });
      setDone(`${child.name} is ${kind === "in" ? "checked in" : "checked out"}. Have a great day!`);
      reset();
      load();
      setTimeout(() => setDone(null), 4000);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setCode("");
    }
  };

  const press = (d) => {
    const next = (code + d).slice(0, 4);
    setCode(next);
    if (next.length === 4) submit(next);
  };

  return (
    <div className="min-h-screen bg-[#0F172A] text-white p-6 sm:p-10">
      <div className="mx-auto max-w-5xl">
        <div className="flex justify-between items-center gap-4">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#FBBF24]">Front Desk Kiosk</p>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold">Teach Play to Learn</h1>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setShowQR(!showQR)}
                    className="rounded-full text-white/70 hover:text-white hover:bg-white/10 font-bold"
                    data-testid="kiosk-qr-toggle">
              <QrCode size={18} className="mr-2" /> {showQR ? "Hide QR" : "Show QR code"}
            </Button>
            <Link to="/admin">
              <Button variant="ghost" className="rounded-full text-white/70 hover:text-white hover:bg-white/10 font-bold"
                      data-testid="kiosk-exit-btn">
                <ArrowLeft size={18} className="mr-2" /> Exit kiosk
              </Button>
            </Link>
          </div>
        </div>

        {showQR && (
          <div className="mt-8 rounded-3xl bg-white/5 border border-white/15 p-8 flex flex-wrap gap-8 items-center tpl-fade-up"
               data-testid="kiosk-qr-panel">
            <div className="bg-white p-5 rounded-3xl shrink-0">
              {qrUrl ? (
                <QRCodeSVG value={qrUrl} size={200} level="M" data-testid="kiosk-qr-code" />
              ) : (
                <div className="h-[200px] w-[200px] grid place-items-center text-sm text-slate-500">Loading…</div>
              )}
            </div>
            <div className="flex-1 min-w-[16rem]">
              <p className="font-display text-2xl font-bold">Scan to sign in from your phone</p>
              <p className="mt-3 text-white/60">
                Print this code and tape it by the door, or leave it on screen. A guardian scans it,
                taps their child, enters their 4-digit code, and they're signed in — no app needed.
              </p>
              {isAdmin(user) && (
                <Button variant="outline" onClick={rotate}
                        className="mt-6 rounded-full font-bold bg-transparent text-white border-white/30 hover:bg-white/10"
                        data-testid="kiosk-qr-rotate">
                  <ArrowsClockwise size={17} className="mr-2" /> Generate a new code
                </Button>
              )}
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <p className="text-xs text-white/70 break-all font-mono select-all" data-testid="kiosk-qr-url">{qrUrl}</p>
                <Button size="sm" variant="ghost"
                        onClick={() => { navigator.clipboard?.writeText(qrUrl); toast.success("Link copied"); }}
                        className="rounded-full text-white/70 hover:text-white hover:bg-white/10 font-bold shrink-0"
                        data-testid="kiosk-qr-copy">
                  <Copy size={16} className="mr-2" /> Copy link
                </Button>
              </div>
            </div>
          </div>
        )}

        {done && (
          <div className="mt-8 rounded-3xl bg-emerald-500/15 border border-emerald-400/40 p-6 flex items-center gap-4 tpl-fade-up"
               data-testid="kiosk-success">
            <CheckCircle size={36} weight="fill" className="text-emerald-300 shrink-0" />
            <p className="text-lg font-bold">{done}</p>
          </div>
        )}

        {!child ? (
          <>
            <p className="mt-10 text-white/60 text-base">Tap your child's name to sign in or out.</p>
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="kiosk-roster">
              {roster.length === 0 && <p className="text-white/60">No active students yet.</p>}
              {roster.map((s) => (
                <button key={s.id} onClick={() => { setChild(s); setKind(s.state === "in" ? "out" : "in"); }}
                        className="text-left rounded-3xl bg-white/5 border border-white/15 p-6 hover:bg-white/10 hover:-translate-y-1 active:scale-[0.98]"
                        style={{ transition: "transform .2s ease, background-color .2s ease" }}
                        data-testid={`kiosk-child-${s.id}`}>
                  <p className="font-display text-2xl font-bold">{s.name}</p>
                  <p className="text-sm text-white/50 mt-1 font-semibold">{s.room}</p>
                  <span className={`mt-4 inline-block text-xs font-bold px-3 py-1.5 rounded-full border ${stateChip[s.state]}`}>
                    {stateLabel[s.state]}
                  </span>
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className="mt-10 grid lg:grid-cols-2 gap-8 items-start" data-testid="kiosk-code-panel">
            <div>
              <p className="text-white/60 font-semibold">Signing {kind === "in" ? "in" : "out"}</p>
              <p className="mt-2 font-display text-4xl sm:text-5xl font-bold">{child.name}</p>
              <div className="mt-6 flex gap-3">
                <Button onClick={() => setKind("in")}
                        className={`h-12 px-6 rounded-full font-bold ${kind === "in" ? "bg-[#10B981] hover:bg-emerald-600" : "bg-white/10 hover:bg-white/20"}`}
                        data-testid="kiosk-kind-in"><SignIn size={18} className="mr-2" /> Sign in</Button>
                <Button onClick={() => setKind("out")}
                        className={`h-12 px-6 rounded-full font-bold ${kind === "out" ? "bg-[#3B82F6] hover:bg-[#2563EB]" : "bg-white/10 hover:bg-white/20"}`}
                        data-testid="kiosk-kind-out"><SignOut size={18} className="mr-2" /> Sign out</Button>
              </div>
              <Input value={guardian} onChange={(e) => setGuardian(e.target.value)}
                     placeholder="Your name (optional)"
                     className="mt-6 h-12 rounded-xl bg-white/10 border-white/20 text-white placeholder:text-white/40 max-w-sm"
                     data-testid="kiosk-guardian" />
              {kind === "out" && child.pickup && (
                <div className="mt-6 rounded-2xl bg-white/5 border border-white/15 p-4 max-w-sm"
                     data-testid="kiosk-pickup-list">
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-white/60">Approved for pickup</p>
                  <ul className="mt-2 space-y-1 text-sm">
                    {child.pickup.authorized.length === 0 && (
                      <li className="text-white/50">Nobody is verified yet — check with the office.</li>
                    )}
                    {child.pickup.authorized.map((p) => (
                      <li key={p.name}>• {p.name} <span className="text-white/50">({p.relationship})</span></li>
                    ))}
                  </ul>
                  {child.pickup.pending.length > 0 && (
                    <>
                      <p className="mt-4 text-xs font-bold uppercase tracking-[0.14em] text-[#FBBF24]">
                        On file, not verified — do not release
                      </p>
                      <ul className="mt-2 space-y-1 text-sm text-white/50">
                        {child.pickup.pending.map((p) => (
                          <li key={p.name} data-testid={`kiosk-pending-${p.name}`}>
                            • {p.name} ({p.relationship}) · source: {p.source}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}
              <Button variant="ghost" onClick={reset}
                      className="mt-6 rounded-full text-white/60 hover:text-white hover:bg-white/10 font-bold"
                      data-testid="kiosk-cancel-btn">Choose a different child</Button>
            </div>

            <div>
              <p className="text-white/60 font-semibold">Enter your 4-digit code</p>
              <div className="mt-4 flex gap-3" data-testid="kiosk-code-dots">
                {[0, 1, 2, 3].map((i) => (
                  <span key={i} className={`h-16 w-14 rounded-2xl border-2 grid place-items-center font-display text-3xl font-bold ${
                    code.length > i ? "border-[#FBBF24] bg-white/10" : "border-white/20"}`}>
                    {code.length > i ? "•" : ""}
                  </span>
                ))}
              </div>
              <div className="mt-6 grid grid-cols-3 gap-3 max-w-xs">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
                  <button key={d} onClick={() => press(String(d))}
                          className="h-20 rounded-2xl bg-white/5 border border-white/15 font-display text-3xl font-bold hover:bg-white/15 active:scale-95"
                          style={{ transition: "transform .15s ease, background-color .2s ease" }}
                          data-testid={`kiosk-key-${d}`}>{d}</button>
                ))}
                <button onClick={() => setCode("")}
                        className="h-20 rounded-2xl bg-white/5 border border-white/15 grid place-items-center hover:bg-white/15"
                        data-testid="kiosk-key-clear"><Backspace size={26} /></button>
                <button onClick={() => press("0")}
                        className="h-20 rounded-2xl bg-white/5 border border-white/15 font-display text-3xl font-bold hover:bg-white/15 active:scale-95"
                        style={{ transition: "transform .15s ease, background-color .2s ease" }}
                        data-testid="kiosk-key-0">0</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
